import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import styles from './Slider.module.scss';

interface Props {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void | undefined;
  className?: string;
  ranges: Array<{
    threshold: number;
    position: number;
    color: string;
  }>;
}

const HANDLE_SIZE = 20;

const precision = 10;

/**
 * Polyfill for IE11
 */
const EPSILON = Number.EPSILON || Math.pow(2, -52);

/**
 * Return the current position based on the value
 */
const interpolateValue = (params: {
  value: number;
  rangeStart: number;
  rangeEnd: number;
  ranges: { threshold: number; position: number }[];
}) => {
  const { value, rangeStart, rangeEnd, ranges } = params;

  const maxPos = rangeEnd - rangeStart;
  const minPos = 0;
  const firstR = ranges[0];
  const lastR = ranges[ranges.length - 1];

  if (value >= lastR.threshold) return maxPos;
  else if (value <= firstR.threshold) return minPos;
  else {
    const index = ranges.findIndex(range => value <= range.threshold);
    if (index < 0) return 0;

    const minT = index > 0 ? ranges[index - 1].threshold : 0;
    const minP = index > 0 ? ranges[index - 1].position : 0;
    const maxT = ranges[index].threshold;
    const maxP = ranges[index].position;

    const partialPercentage = (value - minT) / (maxT - minT);
    const r = minP + partialPercentage * (maxP - minP);
    const position = (r / 100) * (rangeEnd - rangeStart);
    return position > 0 ? Math.round(Math.min(Math.max(position, minPos), maxPos)) : 0;
  }
};

/**
 *  Return the value based on the position on the slider
 */
const interpolateRange = (params: {
  pos: number;
  rangeStart: number;
  rangeEnd: number;
  ranges: { threshold: number; position: number }[];
}) => {
  const { pos, rangeStart, rangeEnd, ranges } = params;

  const percentage = (pos / (rangeEnd - rangeStart)) * 100;
  if (percentage <= 0) return ranges[0].threshold;

  const index = percentage > 100 ? ranges.length - 1 : ranges.findIndex(range => percentage <= range.position);
  if (index < 0) return 0;

  const minT = index === 0 ? 0 : ranges[index - 1].threshold;
  const minP = index === 0 ? 0 : ranges[index - 1].position;
  const maxT = ranges[index].threshold;
  const maxP = ranges[index].position;

  const partialPercentage = (percentage - minP) / (maxP - minP);
  const rawValue = minT + partialPercentage * (maxT - minT);
  return Math.round((rawValue + EPSILON) * 100) / 100;
};

export const CompressedSlider: React.FC<Props> = ({ value, min, max, onChange, className, ranges }) => {
  const [position, setPosition] = useState<number>();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [[start, end], setSliderBoundaries] = useState([0, 0]);

  const minifiedRanges = ranges ? ranges.map(range => ({ threshold: range.threshold, position: range.position })) : [];

  const handleDrag = useCallback(
    e => {
      const pos =
        (e.type === 'touchmove' ? (e as TouchEvent).touches[0].clientX - start : (e as MouseEvent).clientX - start) -
        HANDLE_SIZE / 2;
      const value = interpolateRange({ pos, rangeStart: start, rangeEnd: end, ranges: minifiedRanges });
      onChange(Math.round(Math.min(Math.max(value, min), max) / precision) * precision);
    },
    [onChange, start, end, min, max, minifiedRanges],
  );

  useEffect(() => {
    const calculateLayout = () => {
      if (sliderRef && sliderRef.current) {
        const rect = sliderRef.current.getBoundingClientRect();
        const rangeStart = rect.left;
        const rangeEnd = rect.left + rect.width - HANDLE_SIZE;
        setSliderBoundaries([rangeStart, rangeEnd]);
        setPosition(interpolateValue({ value, rangeStart: start, rangeEnd: end, ranges: minifiedRanges }));
      }
    };

    if (!end) {
      calculateLayout();
    }
    window.addEventListener('resize', calculateLayout);

    return () => {
      window.removeEventListener('resize', calculateLayout);
    };
  }, [sliderRef, value, min, max, start, end, minifiedRanges]);

  useEffect(() => {
    if (start && end) {
      setPosition(interpolateValue({ value, rangeStart: start, rangeEnd: end, ranges: minifiedRanges }));
    }
  }, [value, min, max, start, end, minifiedRanges]);

  return (
    <div
      ref={sliderRef}
      className={`${className ? `${className} ` : ''}` + styles.compressedSlider}
      onClick={handleDrag}
    >
      {ranges?.map((range, index) => {
        const left = (index === ranges.length ? 0 : range.position).toString() + '%';
        const width = (index === ranges.length - 1 ? 0 : ranges[index + 1].position - range.position).toString() + '%';
        return <span className={styles.range} key={index} style={{ left, width, backgroundColor: range.color }} />;
      })}
      <Draggable axis="x" bounds="parent" onDrag={handleDrag} position={{ x: position || 0, y: 0 }}>
        <div className={styles.handle}>
          <span />
        </div>
      </Draggable>
    </div>
  );
};
